// Pure helper functions for the "places added" feedback in the Place Selection
// modal (toast + counter pulse + flight animation).
//
// These are intentionally free of any Vue/component or Vuex dependency so they
// can be imported and exercised directly, mirroring the extraction pattern used
// by the other placeSelection*Helpers.js modules in this folder.
//
// Feature: place-selection "added" feedback

/**
 * Compute how many places were just added given the previous and next
 * selected-place counts.
 *
 * Only a genuine increase is treated as an "add". A decrease (removal) or an
 * unchanged count returns 0, and any non-finite input is coerced to a safe 0
 * delta so callers never fire feedback on a reset-to-empty or on garbage input.
 *
 * @param {number} prev - The previous selected-place count.
 * @param {number} next - The new selected-place count.
 * @returns {number} The positive number of places added, or 0.
 */
export function computeAddedDelta(prev, next) {
  const from = Number.isFinite(prev) ? prev : 0;
  const to = Number.isFinite(next) ? next : 0;
  return to > from ? to - from : 0;
}

/**
 * Build the short inline confirmation shown in the modal footer after places
 * are added, pluralized for the just-added count.
 *
 * Kept concise because it sits next to the running "N place(s) selected"
 * counter in the footer bar; the count itself conveys the new total.
 *
 * @param {number} addedCount - How many places were just added (> 0).
 * @returns {string} The footer confirmation text.
 */
export function formatAddedConfirmation(addedCount) {
  const added = Number.isFinite(addedCount) ? addedCount : 0;
  const noun = added === 1 ? 'place' : 'places';
  return `${added} ${noun} added`;
}
